import { Autocomplete, type AutocompleteProps, Box, type ChipTypeMap, TextField } from "@mui/material";
import { timeSlotGenerator } from "@root/utils/timeSlotGenerator";
import { useMemo } from "react";
import { Controller, type ControllerProps, type FieldPath, type FieldValues } from "react-hook-form";
import { dateGenerator } from "./dateGenerator.js";

export interface ITimeSelectFormProps<
  Value,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
  ChipComponent extends React.ElementType = ChipTypeMap["defaultComponent"],
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<ControllerProps<TFieldValues, TName>, "render">,
    Omit<
      AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent>,
      "defaultValue" | "renderInput" | "options" | "slot"
    > {
  label?: string;
  beginHour?: number;
  endHour?: number;
  slot?: number;
}

export const TimeSelectForm = <
  Value,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
  ChipComponent extends React.ElementType = ChipTypeMap["defaultComponent"],
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  label,
  name,
  beginHour = 8,
  endHour = 20,
  slot = 15,
}: ITimeSelectFormProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent, TFieldValues, TName>) => {
  const timeSlotList = useMemo(() => {
    const beginTime = dateGenerator(beginHour, 0);
    const endTime = dateGenerator(endHour, slot);
    return timeSlotGenerator(beginTime, endTime, slot);
  }, [beginHour, endHour, slot]);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <Autocomplete
          value={value}
          options={timeSlotList}
          fullWidth
          isOptionEqualToValue={(option, value) =>
            option.value.hours === value.value.hours && option.value.minutes === value.value.minutes
          }
          getOptionLabel={(o) => o.label}
          onChange={(_, value) => {
            onChange(value);
          }}
          renderInput={(props) => (
            <TextField
              {...props}
              label={
                <Box
                  sx={{
                    pr: 0.6,
                  }}
                >
                  {label}
                </Box>
              }
              error={!!error}
              helperText={error?.message}
              size="small"
              fullWidth
            />
          )}
        />
      )}
    />
  );
};

export * from "./resolver";
